import React, { useState } from "react";
import { formatMilitaryBranch } from "../../../../../utils/militaryUtils";

const formatSalary = (value) => {
  if (!value) return "Not specified";
  const numericValue = parseInt(value.replace(/[^\d]/g, ""));
  return numericValue.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

const TimelineView = ({ member, onEdit, index = 0 }) => {
  const [filters, setFilters] = useState({
    employment: true,
    education: true,
    military: true,
    active: true,
    completed: true,
  });

  const getAllEvents = () => {
    const events = [];

    if (filters.employment) {
      member.employment?.forEach((job) => {
        if (
          (job.employmentStatus === "active" && filters.active) ||
          (job.employmentStatus !== "active" && filters.completed)
        ) {
          events.push({
            type: "employment",
            startDate: new Date(job.startDate),
            endDate: job.endDate ? new Date(job.endDate) : new Date(),
            title: job.employerName,
            subtitle: job.jobTitle,
            status: job.employmentStatus,
            record: job,
            details: [
              { label: "Type", value: job.employmentType },
              { label: "Salary", value: formatSalary(job.baseAnnualSalary) },
              job.isUnionAffiliated && { label: "Union", value: job.unionName },
            ].filter(Boolean),
          });
        }
      });
    }

    if (filters.education) {
      member.education?.forEach((edu) => {
        if (
          (edu.currentlyEnrolled && filters.active) ||
          (!edu.currentlyEnrolled && filters.completed)
        ) {
          events.push({
            type: "education",
            startDate: new Date(edu.startDate),
            endDate: edu.graduationDate
              ? new Date(edu.graduationDate)
              : new Date(),
            title: edu.institutionName,
            subtitle: edu.educationLevel,
            status: edu.currentlyEnrolled ? "enrolled" : "completed",
            record: edu,
            details: [
              { label: "Field", value: edu.fieldOfStudy },
              { label: "GPA", value: edu.gpa },
            ].filter(Boolean),
          });
        }
      });
    }

    if (filters.military && member.military) {
      events.push({
        type: "military",
        startDate: new Date(member.military.startDate),
        endDate: member.military.endDate
          ? new Date(member.military.endDate)
          : new Date(),
        title: formatMilitaryBranch(member.military.branchOfService),
        subtitle: member.military.rank,
        status: member.military.typeOfService,
        record: member.military,
        details: [
          { label: "Type", value: member.military.typeOfService },
          {
            label: "VA Disability",
            value: member.military.hasVaDisability
              ? `${member.military.vaDisabilityPercentage}%`
              : "No",
          },
          {
            label: "Retirement Pay",
            value: member.military.hasRetirementPay ? "Yes" : "No",
          },
        ],
      });
    }

    return events.sort((a, b) => b.startDate - a.startDate);
  };

  const getEventColor = (type, status) => {
    switch (type) {
      case "employment":
        return status === "active"
          ? "bg-blue-100 border-blue-500"
          : "bg-blue-50 border-blue-300";
      case "education":
        return status === "enrolled"
          ? "bg-purple-100 border-purple-500"
          : "bg-purple-50 border-purple-300";
      case "military":
        return "bg-red-100 border-red-500";
      default:
        return "bg-gray-100 border-gray-500";
    }
  };

  const getEventIcon = (type) => {
    switch (type) {
      case "employment":
        return "💼";
      case "education":
        return "🎓";
      case "military":
        return "🎖️";
      default:
        return "📅";
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
    });
  };

  const events = getAllEvents();

  return (
    <div className="mt-4">
      <div className="mb-4 flex flex-wrap gap-2">
        <button
          onClick={() =>
            setFilters((f) => ({ ...f, employment: !f.employment }))
          }
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            filters.employment
              ? "bg-blue-100 text-blue-800"
              : "bg-gray-100 text-gray-600 dark:text-gray-400"
          }`}
        >
          Employment
        </button>
        <button
          onClick={() => setFilters((f) => ({ ...f, education: !f.education }))}
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            filters.education
              ? "bg-purple-100 text-purple-800"
              : "bg-gray-100 text-gray-600 dark:text-gray-400"
          }`}
        >
          Education
        </button>
        <button
          onClick={() => setFilters((f) => ({ ...f, military: !f.military }))}
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            filters.military
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-600 dark:text-gray-400"
          }`}
        >
          Military
        </button>
        <div className="border-l mx-2" />
        <button
          onClick={() => setFilters((f) => ({ ...f, active: !f.active }))}
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            filters.active
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-600 dark:text-gray-400"
          }`}
        >
          Active
        </button>
        <button
          onClick={() => setFilters((f) => ({ ...f, completed: !f.completed }))}
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            filters.completed
              ? "bg-gray-100 text-gray-800 dark:text-gray-200"
              : "bg-gray-50 text-gray-600 dark:text-gray-400"
          }`}
        >
          Completed
        </button>
      </div>

      <div className="relative">
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200" />

        <div className="space-y-8">
          {events.length === 0 && (
            <div className="text-center py-6 text-gray-500 dark:text-gray-500">
              No career or education history found with the current filters
            </div>
          )}

          {events.map((event, index) => (
            <div key={index} className="relative flex items-start group">
              <div
                className={`flex-shrink-0 w-16 text-xs text-gray-500 dark:text-gray-500 text-right pr-4 pt-0.5`}
              >
                {formatDate(event.startDate)}
              </div>

              <div
                className={`flex-shrink-0 w-4 h-4 rounded-full border-2 mt-1 ${getEventColor(event.type, event.status)}`}
              >
                <span className="sr-only">{event.type}</span>
              </div>

              <div className="flex-grow pl-4">
                <div
                  className={`p-4 rounded-lg border ${getEventColor(event.type, event.status)} transition-all duration-200 hover:shadow-md dark:shadow-none`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <span>{getEventIcon(event.type)}</span>
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">
                          {event.title}
                        </h4>
                      </div>
                      {event.subtitle && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {event.subtitle}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => onEdit(member, event.type, event.record)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-sm text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                  </div>

                  {event.details && (
                    <div className="mt-2 space-y-1">
                      {event.details.map((detail, i) => (
                        <p key={i} className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">{detail.label}:</span>{" "}
                          {detail.value}
                        </p>
                      ))}
                    </div>
                  )}

                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                    {formatDate(event.startDate)} -{" "}
                    {event.endDate ? formatDate(event.endDate) : "Present"}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TimelineView;
